import { memo } from "react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";

import {
  Finding,
  FindingSeverityColor,
  FindingSeverityLabel,
  FindingStateLabel,
  SourceLabel,
} from "stores/FindingStore";

import dayjs from "utility/dayjs";

// -----------------------------------------------------------------------------

const StackOuterSX = {
  alignItems: "stretch",
  borderBottom: (theme: Theme) =>
    `1px ${theme.palette.primary.border ?? ""} solid`,
  cursor: "pointer",
  pr: 2,
  lineHeight: "1",
  "&:hover": {
    background: (theme: Theme) => theme.palette.primary.mainFaded ?? "",
  },
};

const StackOuterSelectedSX = {
  ...StackOuterSX,
  bgcolor: "primary.mainFaded",
};

const IconSX = {
  color: "primary.main",
  height: "100%",
};

const IconDisabledSX = { ...IconSX, color: "primary.mainDisabled" };

const SeverityAndNameSX = { wordBreak: "break-all" };

const SourceSX = {
  opacity: 0.6,
  paddingLeft: 1,
};

const DateSX = { opacity: 0.38 };

const DetailsSX = { p: 2, pl: 0, pr: 0, flexGrow: 1 };

// -----------------------------------------------------------------------------

const OrganizationFindingsInboxFindingRow = memo(
  function OrganizationFindingsInboxFindingRow({
    finding,
    onClick,
    onShiftClick,
    selected,
    canSelect,
  }: OrganizationFindingsInboxFindingRowProps): JSX.Element {
    function handleUserClick(event: MouseEvent): void {
      event.ctrlKey ? onShiftClick(selected) : onClick(false);
    }

    function handleCheckboxClick(event: MouseEvent): void {
      event.preventDefault();
      event.stopPropagation();

      //
      onShiftClick(selected);
    }

    //
    const Icon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;

    //
    return (
      <Stack
        direction="row"
        sx={selected ? StackOuterSelectedSX : StackOuterSX}
      >
        <Box
          sx={{ p: 2, flexShrink: 0 }}
          onClick={canSelect || selected ? (handleCheckboxClick as any) : null}
        >
          <Icon sx={canSelect || selected ? IconSX : IconDisabledSX} />
        </Box>

        <Stack sx={DetailsSX} spacing="1px" onClick={handleUserClick as any}>
          <Typography variant="body1" component="div" sx={SeverityAndNameSX}>
            <Chip
              color={FindingSeverityColor[finding.severity] as any}
              label={FindingSeverityLabel[finding.severity]}
              sx={{ fontWeight: "bold" }}
              size="small"
            />
            <Box component="span" sx={SourceSX}>
              {SourceLabel[finding.source]}
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
            {finding.name}
          </Typography>
          <Typography variant="body1">
            <Box component="span" sx={{ mr: 1 }}>
              {FindingStateLabel[finding.state]}
            </Box>
            <Box component="span" sx={DateSX}>
              {dayjs(finding.create_time).fromNow()}
            </Box>
          </Typography>
        </Stack>
      </Stack>
    );
  }
);

export default OrganizationFindingsInboxFindingRow;

// -----------------------------------------------------------------------------

interface OrganizationFindingsInboxFindingRowProps {
  finding: Finding;
  onClick: Function;
  onShiftClick: Function;
  selected: boolean;
  canSelect: boolean;
}
